import { useEffect, useState } from "react";
import { ApiError, browserTimeZone, type TimeZoneOption } from "@keydials/api-client";
import { Icon } from "@keydials/dial-workspace";
import { api, getAccessToken } from "../auth/AuthContext";

/**
 * The clock one dial keeps (KEY-276), shown to dial admins only.
 *
 * Per DIAL rather than per organisation: one customer can run a dial on a site in Perth and another in
 * Aberdeen, and the person who knows what "06:00" means for a site is the person running that site — the same
 * reasoning, and the same authority, as the shared-device settings next door.
 *
 * Deliberately narrow, and the copy says so: this decides how times ON this dial are read, and changes
 * nothing about how dates and times are DISPLAYED to anyone.
 */
export function TimeZonePanel({
  instanceId,
  timeZoneId,
  canEdit,
  onChanged,
}: {
  instanceId: string;
  timeZoneId: string | null;
  canEdit: boolean;
  onChanged: () => void | Promise<void>;
}) {
  const [zones, setZones] = useState<TimeZoneOption[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // A picker, not a text box: an id the server can't resolve would save happily and only bite later, so the
  // choices come from what the server actually knows.
  useEffect(() => {
    const token = getAccessToken();
    if (!token) return;

    let cancelled = false;
    void api
      .listTimeZones(token)
      .then((list) => {
        if (!cancelled) setZones(list);
      })
      .catch(() => {
        if (!cancelled) setError("Could not load the list of time zones.");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function save(next: string | null) {
    const token = getAccessToken();
    if (!token) return;

    setBusy(true);
    setError(null);
    try {
      await api.setInstanceTimeZone(token, instanceId, next);
      await onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not update the time zone.");
    } finally {
      setBusy(false);
    }
  }

  // The stored zone may not be in the list (a server whose zone database has moved on), so it is offered
  // explicitly rather than silently reading back as UTC.
  const missing = timeZoneId && zones && !zones.some((z) => z.id === timeZoneId) ? timeZoneId : null;
  const { yours, rest } = arrangeZones(zones ?? [], browserTimeZone());

  return (
    <div className="kd-card" style={{ padding: 14, display: "flex", flexDirection: "column", gap: 10 }}>
      <div className="kd-label-overline" style={{ display: "flex", alignItems: "center", gap: 7 }}>
        <Icon name="clock" size={13} />
        Time zone
      </div>

      <label style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 13.5 }}>
        <span>
          The clock this dial keeps
          <span style={{ display: "block", color: "var(--text-muted)", fontSize: 12.5, marginTop: 2, lineHeight: 1.45 }}>
            Anything timed on this dial is read against this zone, so 06:00 means 06:00 where the dial is used,
            and daylight saving is followed for you. It doesn't change how dates and times are shown to you —
            those are the same for everyone.
          </span>
        </span>
        <select
          value={timeZoneId ?? ""}
          disabled={busy || !canEdit || zones === null}
          onChange={(e) => void save(e.target.value === "" ? null : e.target.value)}
          style={{ font: "inherit", fontSize: 13, padding: "5px 8px", borderRadius: 6, border: "var(--border-w) solid var(--border)", background: "var(--surface)", color: "var(--text)" }}
        >
          <option value="">UTC (default)</option>
          {missing && <option value={missing}>{missing} (not recognised by this server)</option>}
          {/* Yours first, then A–Z by place — the server's offset order scattered London among forty "+01:00"s
              (Tim, 12 Oct: "it took me ages to find London"). */}
          {yours && (
            <optgroup label="Your time zone">
              <option value={yours.id}>{zoneLabel(yours)}</option>
            </optgroup>
          )}
          <optgroup label="All time zones, A–Z">
            {rest.map((zone) => (
              <option key={zone.id} value={zone.id}>
                {zoneLabel(zone)}
              </option>
            ))}
          </optgroup>
        </select>
      </label>

      {!canEdit && (
        <div style={{ color: "var(--text-muted)", fontSize: 12.5 }}>
          Changing this needs admin of the whole dial, not one section.
        </div>
      )}
      {error && <div role="alert" style={{ color: "var(--st-overdue)", fontSize: 12.5 }}>{error}</div>}
    </div>
  );
}

/** "London (Europe) · UTC+01:00" — the place first, since that's what a person looks for. */
export function zoneLabel(zone: TimeZoneOption): string {
  const parts = zone.id.split("/");
  const place = parts[parts.length - 1].replace(/_/g, " ");
  const region = parts.length > 1 ? parts.slice(0, -1).join(" / ").replace(/_/g, " ") : null;
  return `${place}${region ? ` (${region})` : ""} · UTC${zone.utcOffset}`;
}

/** This device's zone pulled to the top (when the server knows it); everything else A–Z by place name. */
export function arrangeZones(zones: readonly TimeZoneOption[], deviceZone: string | null): { yours: TimeZoneOption | null; rest: TimeZoneOption[] } {
  const yours = deviceZone ? zones.find((z) => z.id === deviceZone) ?? null : null;
  const rest = zones
    .filter((z) => z !== yours)
    .slice()
    .sort((a, b) => zoneLabel(a).localeCompare(zoneLabel(b), "en", { sensitivity: "base" }));
  return { yours, rest };
}
